import React from 'react';
import { Button, useTheme, useMediaQuery } from '@mui/material';

const BaseButton = ({
  variant = 'contained',
  color = 'primary',
  size = 'medium',
  sx = {},
  children,
  gradient = false,
  fullWidth = false,
  ...props
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Styles par défaut selon la variante
  const getBaseStyles = () => {
    const baseStyles = {
      borderRadius: '12px',
      fontWeight: 600,
      textTransform: 'none',
      fontSize: isMobile ? '14px' : '16px',
      px: 3,
      py: 1.5,
      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
      ...(fullWidth && { width: '100%' })
    };

    if (variant === 'contained') {
      if (gradient) {
        return {
          ...baseStyles,
          background: 'linear-gradient(135deg, #3F51B5 0%, #5C6BC0 100%)',
          color: 'white',
          border: 'none',
          boxShadow: '0 4px 12px rgba(63, 81, 181, 0.3)',
          '&:hover': {
            background: 'linear-gradient(135deg, #303F9F 0%, #3F51B5 100%)',
            transform: 'translateY(-2px)',
            boxShadow: '0 6px 16px rgba(63, 81, 181, 0.4)'
          },
          '&:active': {
            transform: 'translateY(-1px)',
            boxShadow: '0 4px 12px rgba(63, 81, 181, 0.3)'
          },
          '&:disabled': {
            background: 'rgba(0,0,0,0.12)',
            color: 'rgba(0,0,0,0.26)',
            boxShadow: 'none',
            transform: 'none'
          }
        };
      }
      
      return {
        ...baseStyles,
        backgroundColor: color === 'primary' ? '#3F51B5' : theme.palette[color]?.main,
        color: 'white',
        boxShadow: '0 2px 8px rgba(63, 81, 181, 0.2)',
        '&:hover': {
          backgroundColor: color === 'primary' ? '#303F9F' : theme.palette[color]?.dark,
          transform: 'translateY(-1px)',
          boxShadow: '0 4px 12px rgba(63, 81, 181, 0.3)'
        },
        '&:active': {
          transform: 'translateY(0px)',
          boxShadow: '0 2px 8px rgba(63, 81, 181, 0.2)'
        }
      };
    }

    if (variant === 'outlined') {
      return {
        ...baseStyles,
        backgroundColor: 'transparent',
        color: color === 'primary' ? '#3F51B5' : theme.palette[color]?.main,
        border: `2px solid ${color === 'primary' ? '#3F51B5' : theme.palette[color]?.main}`,
        '&:hover': {
          backgroundColor: color === 'primary' ? 'rgba(63, 81, 181, 0.05)' : theme.palette[color]?.main + '0D',
          borderColor: color === 'primary' ? '#303F9F' : theme.palette[color]?.dark,
          transform: 'translateY(-1px)',
          boxShadow: `0 2px 8px ${color === 'primary' ? 'rgba(63, 81, 181, 0.15)' : theme.palette[color]?.main + '26'}`
        }
      };
    }

    if (variant === 'text') {
      return {
        ...baseStyles,
        backgroundColor: 'transparent',
        color: color === 'primary' ? '#3F51B5' : theme.palette[color]?.main,
        border: 'none',
        '&:hover': {
          backgroundColor: color === 'primary' ? 'rgba(63, 81, 181, 0.05)' : theme.palette[color]?.main + '0D',
          transform: 'translateY(-1px)'
        }
      };
    }

    return baseStyles;
  };

  return (
    <Button
      variant={variant}
      color={color}
      size={size}
      sx={{
        ...getBaseStyles(),
        ...sx
      }}
      {...props}
    >
      {children}
    </Button>
  );
};

export default BaseButton; 